import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from 'react-query';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { 
  UserCircleIcon, 
  EnvelopeIcon, 
  PhoneIcon,
  MapPinIcon,
  GlobeAltIcon,
  CakeIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const UserProfile = () => {
  const { user: authUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [useFallbackData, setUseFallbackData] = useState(false);
  
  // Fallback data for development/testing
  const fallbackProfile = useMemo(() => ({
    id: 1,
    name: authUser?.name || 'John Doe',
    email: authUser?.email || 'john.doe@example.com',
    phone: '+1234567890',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    address: '123 Main St, Apt 4B',
    city: 'Nairobi',
    country: 'Kenya',
    dateOfBirth: '1985-06-15',
    preferences: {
      tourTypes: ['Wildlife Safari', 'Beach', 'Cultural'],
      notifications: true,
      newsletter: true
    },
    memberSince: '2023-01-15'
  }), [authUser?.name, authUser?.email]);
  
  // Fetch user profile
  const fetchUserProfile = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const api = axios.create({
        baseURL: apiUrl
      });
      const response = await api.get('/api/v1/user/profile', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  };
  
  // User profile query
  const { 
    data: profile, 
    isLoading, 
    error,
    refetch 
  } = useQuery('userProfile', fetchUserProfile, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    onError: () => {
      setShowError(true);
    }
  });
  
  // Update profile mutation
  const updateProfileMutation = useMutation(
    async (profileData) => {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const api = axios.create({
        baseURL: apiUrl
      });
      const response = await api.put('/api/v1/user/profile', profileData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        refetch();
        setIsEditing(false);
        setUpdateSuccess(true);
        setTimeout(() => setUpdateSuccess(false), 3000);
      }
    }
  );

  const handleTryAgain = () => {
    setShowError(false);
    refetch();
  };

  const handleUseFallbackData = () => {
    setUseFallbackData(true);
    setShowError(false);
  };
  
  // Use fallback data when in development or when there's an error
  const profileData = useFallbackData ? fallbackProfile : (profile?.data || fallbackProfile);
  
  // Profile form validation schema
  const profileValidationSchema = Yup.object({
    name: Yup.string()
      .required('Full name is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    phone: Yup.string()
      .required('Phone number is required'),
    address: Yup.string()
      .required('Address is required'),
    city: Yup.string()
      .required('City is required'),
    country: Yup.string()
      .required('Country is required'),
    dateOfBirth: Yup.date()
      .required('Date of birth is required')
      .max(new Date(), 'Date of birth cannot be in the future'),
    'preferences.notifications': Yup.boolean(),
    'preferences.newsletter': Yup.boolean(),
    'preferences.tourTypes': Yup.array()
      .of(Yup.string())
      .min(1, 'Select at least one tour type')
  });
  
  // Initialize profile form
  const profileFormik = useFormik({
    initialValues: {
      name: profileData.name,
      email: profileData.email,
      phone: profileData.phone || '',
      address: profileData.address || '',
      city: profileData.city || '',
      country: profileData.country || '',
      dateOfBirth: profileData.dateOfBirth || '',
      'preferences.notifications': profileData.preferences?.notifications || false,
      'preferences.newsletter': profileData.preferences?.newsletter || false,
      'preferences.tourTypes': profileData.preferences?.tourTypes || []
    },
    validationSchema: profileValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateProfileMutation.mutate({
        name: values.name,
        email: values.email,
        phone: values.phone,
        address: values.address,
        city: values.city,
        country: values.country,
        dateOfBirth: values.dateOfBirth,
        preferences: {
          notifications: values['preferences.notifications'],
          newsletter: values['preferences.newsletter'],
          tourTypes: values['preferences.tourTypes']
        }
      });
    }
  });
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Available tour types
  const tourTypes = [
    'Wildlife Safari',
    'Beach',
    'Cultural',
    'Adventure',
    'Hiking',
    'City Tour',
    'Historical',
    'Food & Culinary'
  ];
  
  // Handle tour type selection
  const handleTourTypeChange = (type) => {
    const currentTypes = profileFormik.values['preferences.tourTypes'];
    if (currentTypes.includes(type)) {
      profileFormik.setFieldValue(
        'preferences.tourTypes',
        currentTypes.filter(t => t !== type)
      );
    } else {
      profileFormik.setFieldValue(
        'preferences.tourTypes',
        [...currentTypes, type]
      );
    }
  };
  
  // Render loading state
  if (isLoading && !useFallbackData) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }
  
  // Render error state
  if (showError && !useFallbackData) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-8 rounded-md text-center">
        <div className="flex flex-col items-center justify-center">
          <div className="rounded-full bg-red-100 p-3 mb-4">
            <svg className="h-12 w-12 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-red-700 mb-2">Something went wrong</h2>
          <p className="text-red-600 mb-6">Failed to load your profile. Please try again later.</p>
          <div className="flex space-x-4">
            <button 
              onClick={handleTryAgain}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              <ArrowPathIcon className="w-5 h-5 mr-2" />
              Try Again
            </button>
            <button 
              onClick={handleUseFallbackData}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Use Sample Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success message */}
      {updateSuccess && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                Profile updated successfully!
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-primary text-white p-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center text-primary text-4xl font-bold mb-4 md:mb-0 md:mr-6">
              {profileData.name.charAt(0).toUpperCase()}
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-bold">{profileData.name}</h2>
              <p className="text-primary-light">{profileData.email}</p>
              <p className="text-sm mt-1">Member since {formatDate(profileData.memberSince)}</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Personal Information</h3>
            <button
              type="button"
              onClick={() => setIsEditing(!isEditing)}
              className="px-4 py-2 border border-primary rounded-md text-primary hover:bg-primary-50 transition-colors"
            >
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>
          
          {isEditing ? (
            <form onSubmit={profileFormik.handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={`w-full px-3 py-2 border rounded-md ${
                      profileFormik.touched.name && profileFormik.errors.name
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    value={profileFormik.values.name}
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                  />
                  {profileFormik.touched.name && profileFormik.errors.name && (
                    <p className="mt-1 text-sm text-red-500">{profileFormik.errors.name}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`w-full px-3 py-2 border rounded-md ${
                      profileFormik.touched.email && profileFormik.errors.email
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    value={profileFormik.values.email}
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                  />
                  {profileFormik.touched.email && profileFormik.errors.email && (
                    <p className="mt-1 text-sm text-red-500">{profileFormik.errors.email}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className={`w-full px-3 py-2 border rounded-md ${
                      profileFormik.touched.phone && profileFormik.errors.phone
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    value={profileFormik.values.phone}
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                  />
                  {profileFormik.touched.phone && profileFormik.errors.phone && (
                    <p className="mt-1 text-sm text-red-500">{profileFormik.errors.phone}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    className={`w-full px-3 py-2 border rounded-md ${
                      profileFormik.touched.dateOfBirth && profileFormik.errors.dateOfBirth
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    value={profileFormik.values.dateOfBirth}
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                  />
                  {profileFormik.touched.dateOfBirth && profileFormik.errors.dateOfBirth && (
                    <p className="mt-1 text-sm text-red-500">{profileFormik.errors.dateOfBirth}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    className={`w-full px-3 py-2 border rounded-md ${
                      profileFormik.touched.address && profileFormik.errors.address
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    value={profileFormik.values.address}
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                  />
                  {profileFormik.touched.address && profileFormik.errors.address && (
                    <p className="mt-1 text-sm text-red-500">{profileFormik.errors.address}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    className={`w-full px-3 py-2 border rounded-md ${
                      profileFormik.touched.city && profileFormik.errors.city
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    value={profileFormik.values.city}
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                  />
                  {profileFormik.touched.city && profileFormik.errors.city && (
                    <p className="mt-1 text-sm text-red-500">{profileFormik.errors.city}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    className={`w-full px-3 py-2 border rounded-md ${
                      profileFormik.touched.country && profileFormik.errors.country
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    value={profileFormik.values.country}
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                  />
                  {profileFormik.touched.country && profileFormik.errors.country && (
                    <p className="mt-1 text-sm text-red-500">{profileFormik.errors.country}</p>
                  )}
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 mb-3">Tour Preferences</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {tourTypes.map((type) => (
                    <div key={type} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`type-${type}`}
                        checked={profileFormik.values['preferences.tourTypes'].includes(type)}
                        onChange={() => handleTourTypeChange(type)}
                        className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <label htmlFor={`type-${type}`} className="ml-2 text-sm text-gray-700">
                        {type}
                      </label>
                    </div>
                  ))}
                </div>
                {profileFormik.touched['preferences.tourTypes'] && profileFormik.errors['preferences.tourTypes'] && (
                  <p className="mt-1 text-sm text-red-500">{profileFormik.errors['preferences.tourTypes']}</p>
                )}
              </div>
              
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 mb-3">Communication Preferences</h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="preferences.notifications"
                      name="preferences.notifications"
                      checked={profileFormik.values['preferences.notifications']}
                      onChange={profileFormik.handleChange}
                      className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <label htmlFor="preferences.notifications" className="ml-2 text-sm text-gray-700">
                      Receive notifications about bookings and tours
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="preferences.newsletter"
                      name="preferences.newsletter"
                      checked={profileFormik.values['preferences.newsletter']}
                      onChange={profileFormik.handleChange}
                      className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <label htmlFor="preferences.newsletter" className="ml-2 text-sm text-gray-700">
                      Subscribe to newsletter for deals and updates
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="mr-3 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
                  disabled={updateProfileMutation.isLoading}
                >
                  {updateProfileMutation.isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start">
                  <UserCircleIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Full Name</h4>
                    <p className="mt-1">{profileData.name}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Email Address</h4>
                    <p className="mt-1">{profileData.email}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <PhoneIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Phone Number</h4>
                    <p className="mt-1">{profileData.phone || 'Not provided'}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CakeIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Date of Birth</h4>
                    <p className="mt-1">
                      {profileData.dateOfBirth ? formatDate(profileData.dateOfBirth) : 'Not provided'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Address</h4>
                    <p className="mt-1">{profileData.address || 'Not provided'}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <GlobeAltIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Location</h4>
                    <p className="mt-1">
                      {profileData.city && profileData.country
                        ? `${profileData.city}, ${profileData.country}`
                        : 'Not provided'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-3">Tour Preferences</h4>
                <div className="flex flex-wrap gap-2">
                  {profileData.preferences?.tourTypes?.length > 0 ? (
                    profileData.preferences.tourTypes.map((type) => (
                      <span
                        key={type}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-light text-primary"
                      >
                        {type}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500">No preferences set</p>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-3">Communication Preferences</h4>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className={`h-4 w-4 rounded-sm mr-2 ${
                      profileData.preferences?.notifications ? 'bg-primary' : 'bg-gray-200'
                    }`}></span>
                    <p className="text-sm text-gray-700">
                      {profileData.preferences?.notifications
                        ? 'Receiving notifications about bookings and tours'
                        : 'Not receiving notifications about bookings and tours'}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <span className={`h-4 w-4 rounded-sm mr-2 ${
                      profileData.preferences?.newsletter ? 'bg-primary' : 'bg-gray-200'
                    }`}></span>
                    <p className="text-sm text-gray-700">
                      {profileData.preferences?.newsletter
                        ? 'Subscribed to newsletter for deals and updates'
                        : 'Not subscribed to newsletter for deals and updates'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
